import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import DefaultTabs from '../../../components/Accessibility/DefaultTabs';
import DefaultText from '../../../components/Accessibility/DefaultText';
import PageTitle from '../../../components/Accessibility/PageTitle';
import VoltarButton from '../../../components/Accessibility/VoltarButton';
import { PerguntaItem } from '../../../interfaces/entities/SaudeBemEstar/AutoAvaliacao/PerguntaItem';
import { getAceiteSaudeEmocional, getHistoricoAvaliacao, getQuestoesAvaliacao, postRepostaQuestionarioAvaliacao } from '../../../services/SaudeBemEstar/SaudeEmocionalService';
import AutoAvaliacaoPergunta from './components/AutoAvaliacaoPergunta';
import DesistirModal from './components/DesistirModal/DesistirModal';
import TermoDeUsoModal from './components/TermoDeUsoModal';
import './styles.css';

const AutoAvaliacaoQuestoes: React.FC = () => {

  const { idRespQuestBenef } = useParams();
  const navigate = useNavigate();
  const [perguntas, setPerguntas] = useState<PerguntaItem[]>([]);
  const [respostas, setRespostas] = useState<any>({});
  const [step, setStep] = useState<number>(0);
  const [showTermosModal, setShowTermosModal] = useState<boolean>(false);
  const [showDesistirModal, setShowDesistirModal] = useState<boolean>(false);
  const [loadingQuestoes, setLoadingQuestoes] = useState<boolean>(true);
  const [loadingTermo, setLoadingTermo] = useState<boolean>(true);
  const stepSize = 7;

  useEffect(() => {
    if (!!idRespQuestBenef) {
      handleAnsweredAutoAvaliacao(parseInt(idRespQuestBenef));
    } else {
      handleNewAutoAvaliacao();
    }
  }, []);

  useEffect(() => {
    scrollTop();
  }, [step]);

  const onOptionSelected = (idQuestao: string, indexOpcao: number) => {
    const newRespostas = { ...respostas }
    newRespostas[idQuestao] = indexOpcao
    setRespostas(newRespostas);
  }

  const handleNewAutoAvaliacao = async () => {
    setLoadingQuestoes(true);
    setLoadingTermo(true);

    getAceiteSaudeEmocional()
      .then(response => {
        setShowTermosModal(!response);
      })
      .catch(console.log)
      .finally(() => {
        setLoadingTermo(false);
      })

    getQuestoesAvaliacao()
      .then(setPerguntas)
      .catch(console.log)
      .finally(() => {
        setLoadingQuestoes(false);
      })
  }

  const handleAnsweredAutoAvaliacao = async (idRespQuestBenef: number) => {
    setLoadingQuestoes(true);
    const response = await getHistoricoAvaliacao(idRespQuestBenef);
    if (response?.status === 200) {
      setPerguntas(response.data);
    } else {
      console.log(response);
    }
    setLoadingQuestoes(false);
    setLoadingTermo(false);
  }

  const handleFinalize = () => {
    return !!idRespQuestBenef
      ? navigate(`/saudeBemEstar/unimedAcolhe/autoAvaliacao/`)
      : postAutoAvaliacao();
  }

  const postAutoAvaliacao = () => {
    const payload = perguntas.map(item => {
      const resposta = respostas[item.idQuestaoTeleOrient]
      return {
        resposta: resposta.toString(),
        idRespQuestBenef: item.idRespQuestBenef,
        lstQuestoesTriagem: item.lstQuestoesTriagem.map((val, i) => ({
          idItemQuestaoTeleOrient: val.idItemQuestaoTeleOrient,
          resposta: i === resposta ? 'S' : 'N'
        }))
      }
    })
    postRepostaQuestionarioAvaliacao(payload)
      .then(response => {
        console.log(response);
        navigate(`/saudeBemEstar/unimedAcolhe/autoAvaliacao/resultado/${perguntas[0].idRespQuestBenef}`)
      })
      .catch(console.log)
  }


  const nextStep = () => {
    setStep(step + 1);
  }

  const prevStep = () => {
    setStep(step - 1);
  }

  const isQuestCompleto = (perguntas: PerguntaItem[]) => {
    if (!!idRespQuestBenef) {
      return true;
    } else {
      for (const pergunta of perguntas) {
        if (respostas[pergunta.idQuestaoTeleOrient] === undefined) {
          return false;
        }
      }
      return true;
    }
  }

  const scrollTop = () => {
    const element = document.getElementById('scroll-ref');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const totalSteps = Math.ceil(perguntas.length / stepSize);
  const lastStep = step + 1 === totalSteps;
  const perguntasStep = perguntas.slice(step * stepSize, (step * stepSize) + stepSize);

  const lastStepText = !!idRespQuestBenef ? 'Voltar para o início' : 'FINALIZAR';

  const backStep = () => {
    if (step === 0) {
      return !!idRespQuestBenef ? window.history.back() : setShowDesistirModal(true);
    } else {
      prevStep();
    }
  }

  const handleClose = () => {
    setShowTermosModal(false)
    navigate('/saudeBemEstar/unimedAcolhe/autoAvaliacao/info?isReturning=true')
  }

  return (
    <div id='scroll-ref' className='page-container'>
      <PageTitle text="Avaliação" />
      <DefaultTabs
        skipConfirmation
        step={step} 
        stepLabels={["Passo 1", "Passo 2", "Passo 3"]}
        onTabClick={setStep}
      />
      <div className="perguntas-container">
        <DefaultText
          text="Escolha a opção que melhor indica o quanto a afirmação abaixo aplica-se a você na última semana." />
        {
          !loadingQuestoes && !loadingTermo ?
            (perguntasStep.map(item => (
              <div key={item.idQuestaoTeleOrient}>
                <AutoAvaliacaoPergunta selected={respostas[item.idQuestaoTeleOrient]} onSelected={onOptionSelected} data={item} readOnly={!!idRespQuestBenef} />
              </div>
            )))
            : <CircularProgress />
        }
      </div>

      <DefaultButton
        variant='contained'
        text={lastStep ? lastStepText : 'Próximo'}
        onClick={lastStep ? handleFinalize : nextStep}
        disabled={!isQuestCompleto(perguntasStep)} />

      <VoltarButton
      description={step === 0 ? 'Voltar para menu de Avaliação' : 'Voltar ao passo anterior'}
        onClick={backStep} />

      {!idRespQuestBenef &&
        <>
          <TermoDeUsoModal open={showTermosModal} onClose={handleClose} onTermoAceito={() => { setShowTermosModal(false) }} />

          <DesistirModal
            showModal={showDesistirModal}
            handleCancelarClick={() => { setShowDesistirModal(false) }}
            handleDesistirClick={() => { navigate('/saudeBemEstar/unimedAcolhe/autoAvaliacao/info?isReturning=true') }}
          />
        </>
      }

    </div>
  )
}

export default AutoAvaliacaoQuestoes;